/**
 * Phase 7C-6C -- how this Website renders WHO did something and WHEN.
 *
 * One module rather than a helper per screen, because the two questions
 * it answers were previously answered differently on every screen: the
 * purchase-order list hardcoded a manager's name, the goods-receive slip
 * printed "ผู้ดูแลระบบ", the shift summary said "แคชเชียร์", and the
 * consignment history rendered whatever string the server happened to
 * send. Those were four different lies about the same two facts.
 *
 * THE TIME RULE
 * ----------------------------------------------------------------------
 * Backend Phase 7C-6B settled the contract (Plan B): a business or
 * history timestamp is Thailand wall-clock time, and `created_at_iso`
 * says so explicitly with a `+07:00` offset. `2026-09-08T14:30:00+07:00`
 * means half past two in the afternoon in the shop, and it must render
 * as half past two -- on the till, on a laptop, and on a reviewer's
 * machine in another country.
 *
 * That last clause is why this does not use `toLocaleString()` with the
 * machine's timezone: the same event would then read 14:30 in the shop
 * and 07:30 in London, and a support conversation about "the 14:30 entry"
 * would stop making sense. Rendering is pinned to `Asia/Bangkok`, so the
 * output depends only on the event, never on who is looking.
 *
 * No arithmetic is performed anywhere in this file. Nothing adds or
 * subtracts seven hours. The offset in the string is what makes the
 * instant unambiguous, and `Intl` does the rest.
 */

/** The shop's timezone. Fixed since 1976 -- Thailand observes no DST. */
const DISPLAY_TIME_ZONE = 'Asia/Bangkok';

/**
 * Shown where a record genuinely predates authentication.
 *
 * Rows written before Phase 7C-5 carry no operator, and there is no way
 * to learn who made them -- not from the current login, not from the
 * vendor, not from a neighbouring row. Saying so plainly is the only
 * honest option; inventing a plausible name would quietly turn an audit
 * trail into fiction.
 */
export const UNKNOWN_ACTOR_LEGACY = 'ไม่ระบุผู้ทำรายการ (ก่อนเปิดใช้ระบบยืนยันตัวตน)';

/**
 * The shorter form, for places where a missing name is not necessarily
 * a pre-authentication row -- a list column, a draft, a record whose
 * contract this phase has not proven. Claiming "before authentication"
 * there would be asserting something unverified.
 */
export const UNKNOWN_ACTOR_SHORT = 'ไม่ระบุ';

/**
 * Operator names that are not names.
 *
 * Before Phase 7C-5 every Consignment write read `user_name` from the
 * request body and defaulted to the literal string `ระบบ` ("system")
 * when it was absent -- which it always was, because neither client ever
 * sent it. Those rows are in the database on Staging and in the shop's
 * data: `created_by` is null, and `recorded_by_name` is `ระบบ`.
 *
 * A row like that has no operator. Rendering the placeholder verbatim
 * puts a fabricated actor on screen just as surely as the hardcoded
 * names this phase removed from the client -- the fabrication simply
 * happens to be persisted. So it is treated as the absence it is.
 *
 * MATCHED EXACTLY, NEVER AS A SUBSTRING. Employee E0008 on Staging is
 * named "Dev01 ระบบ", and a real person must never be erased by a rule
 * meant for a placeholder.
 */
const PLACEHOLDER_ACTOR_NAMES = new Set(['ระบบ']);

/** What a missing or unreadable timestamp renders as. */
export const EMPTY_TIME = '-';

/**
 * A naive `YYYY-MM-DD HH:MM[:SS]` string -- the legacy shape, carrying no
 * offset. Matched deliberately rather than handed to `new Date()`,
 * because engines disagree about whether such a string is local or UTC,
 * and being wrong shifts the shop's history by seven hours.
 */
const NAIVE_TIMESTAMP = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/;

/**
 * A `DD-MM-YYYY HH:MM[:SS]` string -- what the Purchase Order, Goods
 * Receive and Check Stock endpoints actually return in `create_date` and
 * `update_date`. Those three do NOT carry a Plan B `+07:00` field; the
 * value arrives pre-formatted by MySQL as
 * `DATE_FORMAT(x.create_date, "%d-%m-%Y %H:%i:%S")`, day first.
 *
 * Day-first is read from the Backend format string, not guessed: handing
 * "08-09-2026 15:42:03" to `new Date()` yields Invalid Date in Chrome and
 * a September-the-8th-or-August-the-9th coin flip elsewhere. The ranges
 * are still checked, so a value that cannot be day-first renders as `-`
 * rather than silently becoming a different date.
 *
 * Reprinting these digits performs no timezone arithmetic. Backend has
 * proven the stored value is Bangkok wall clock (the DB server clock is
 * Asia/Bangkok), so the digits are already what the shop saw.
 */
const DAY_FIRST_TIMESTAMP = /^(\d{2})[-/](\d{2})[-/](\d{4})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?$/;

const bangkokParts = (date: Date): { day: string; month: string; year: string; hour: string; minute: string } | null => {
    try {
        const parts = new Intl.DateTimeFormat('en-GB', {
            timeZone: DISPLAY_TIME_ZONE,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        }).formatToParts(date);
        const find = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
        const result = {
            day: find('day'),
            month: find('month'),
            year: find('year'),
            // Some engines render midnight as "24" in hour12:false.
            hour: find('hour') === '24' ? '00' : find('hour'),
            minute: find('minute'),
        };
        return result.year && result.month && result.day ? result : null;
    } catch (e) {
        return null;
    }
};

/**
 * Formats an event timestamp as `DD/MM/YYYY HH:mm น.` in Thailand time.
 *
 * Accepts both shapes the Backend currently emits, and treats them
 * differently on purpose:
 *
 *   `2026-09-08T14:30:00+07:00`  the Plan B field (`created_at_iso`).
 *                                The offset makes it a real instant, so
 *                                it is parsed and rendered in Bangkok --
 *                                correct even if the offset were ever
 *                                something else.
 *   `2026-09-08 14:30:00`        the legacy field (`created_at`). It has
 *                                no offset, so the digits are read
 *                                directly and reprinted. Handing this to
 *                                `new Date()` would let the engine guess
 *                                a timezone; reading it literally cannot
 *                                be wrong, because Backend has proven the
 *                                value is already Bangkok wall clock.
 *
 * Never throws. Anything unrecognisable renders as `-`, because a
 * history table that crashes is worse than one cell that says nothing.
 */
export const formatEventTime = (value?: string | null): string => {
    if (value === null || value === undefined) return EMPTY_TIME;
    const raw = String(value).trim();
    if (!raw) return EMPTY_TIME;

    const naive = NAIVE_TIMESTAMP.exec(raw);
    if (naive) {
        const [, year, month, day, hour, minute] = naive;
        return `${day}/${month}/${year} ${hour}:${minute} น.`;
    }

    const dayFirst = DAY_FIRST_TIMESTAMP.exec(raw);
    if (dayFirst) {
        const [, day, month, year, hour, minute] = dayFirst;
        // Reject anything that cannot be the day-first shape Backend
        // documents, rather than quietly reading it the other way round.
        if (Number(day) >= 1 && Number(day) <= 31 && Number(month) >= 1 && Number(month) <= 12) {
            return hour === undefined
                ? `${day}/${month}/${year}`
                : `${day}/${month}/${year} ${hour}:${minute} น.`;
        }
        return EMPTY_TIME;
    }

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return EMPTY_TIME;

    const parts = bangkokParts(parsed);
    if (!parts) return EMPTY_TIME;
    return `${parts.day}/${parts.month}/${parts.year} ${parts.hour}:${parts.minute} น.`;
};

/**
 * Formats an operator name, or says honestly that there isn't one.
 *
 * [fallback] lets a caller choose between the two truths: a consignment
 * history row with no actor really does predate authentication, while a
 * blank name in a list column might mean something else entirely, and
 * only the first of those earns the long sentence.
 */
export const formatActor = (
    name?: string | null,
    fallback: string = UNKNOWN_ACTOR_LEGACY,
): string => {
    if (name === null || name === undefined) return fallback;
    const trimmed = String(name).trim();
    if (!trimmed) return fallback;
    if (PLACEHOLDER_ACTOR_NAMES.has(trimmed)) return fallback;
    return trimmed;
};

/**
 * True when the value is a real recorded operator.
 *
 * Lets a screen style an unknown actor differently -- greyed out, say --
 * without re-deriving the emptiness rule and drifting from `formatActor`.
 */
export const hasRealActor = (name?: string | null): boolean => {
    if (name === null || name === undefined) return false;
    const trimmed = String(name).trim();
    return trimmed.length > 0 && !PLACEHOLDER_ACTOR_NAMES.has(trimmed);
};
